import "./Pagination.css";
import PageSizeSelector from "./PageSizeSelector";

interface PaginationProps {
  currPage: number;
  onPageChange: (page: number) => void;
  pageLimit: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  totalItems?: number; // Total number of items for "Showing X-Y of Z" display
  itemLabel?: string; // Label for items (e.g., "matters", "items")
}

const Pagination = ({ 
  currPage, 
  onPageChange, 
  pageLimit, 
  pageSize, 
  onPageSizeChange,
  totalItems,
  itemLabel = 'items'
}: PaginationProps) => {
  const handlePageDecrement = (): void => {
    if (currPage > 1) {
      onPageChange(currPage - 1);
    }
  };

  const handlePageIncrement = (): void => {
    if (currPage < pageLimit) {
      onPageChange(currPage + 1);
    }
  };

  const handlePageClick = (page: number): void => {
    if (page >= 1 && page <= pageLimit && page !== currPage) {
      onPageChange(page);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const maxVisible = 5; // Show up to 5 page buttons
    
    if (pageLimit <= maxVisible) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= pageLimit; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      let start = Math.max(1, currPage - 2);
      let end = Math.min(pageLimit, currPage + 2);
      
      // Adjust if we're near the beginning or end
      if (end - start < maxVisible - 1) {
        if (start === 1) {
          end = Math.min(pageLimit, start + maxVisible - 1);
        } else {
          start = Math.max(1, end - maxVisible + 1);
        }
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  // Calculate "Showing X-Y of Z" display
  const getShowingRange = () => {
    if (!totalItems) return null;
    const start = (currPage - 1) * pageSize + 1;
    const end = Math.min(currPage * pageSize, totalItems);
    return `Showing ${start}-${end} of ${totalItems} ${itemLabel}`;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="footer">
      <div className="pagination-controls">
        <div className="pagination-left">
          <PageSizeSelector 
            pageSize={pageSize} 
            onPageSizeChange={onPageSizeChange} 
          />
          {totalItems && (
            <span className="pagination-count">{getShowingRange()}</span>
          )}
        </div>
        <div className="pagination-navigation">
          <button 
            className="pagination-button pagination-button-text" 
            onClick={handlePageDecrement}
            disabled={currPage === 1}
            aria-label="Previous page"
          >
            Previous
          </button>
          {pageNumbers.map((pageNum) => (
            <button
              key={pageNum}
              className={`pagination-button pagination-page-button ${
                pageNum === currPage ? 'pagination-page-active' : ''
              }`}
              onClick={() => handlePageClick(pageNum)}
              aria-label={`Go to page ${pageNum}`}
              aria-current={pageNum === currPage ? 'page' : undefined}
            >
              {pageNum}
            </button>
          ))}
          <button 
            className="pagination-button pagination-button-text" 
            onClick={handlePageIncrement}
            disabled={currPage === pageLimit}
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
